import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  RadioButtonUnchecked as PendingIcon,
} from '@mui/icons-material';

export interface RedemptionConnectStatus {
  connectPayoutsEnabled: boolean;
  requireConnectSetup: boolean;
  devSimulationAvailable: boolean;
  stripeConfigured: boolean;
  usedSimulation: boolean;
  hasConnectAccount: boolean;
  connectAccountId?: string | null;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  tinStatus: string;
  isReadyForPayout: boolean;
  disabledReason?: string | null;
  simulationMode?: string | null;
  payoutBlockers: string[];
}

export interface RedemptionConnectOnboardResult {
  success: boolean;
  usedLiveStripe?: boolean;
  simulationAvailable?: boolean;
  onboardingUrl?: string;
  errorMessage?: string;
}

export interface RedemptionConnectSimulateResult {
  success: boolean;
  outcome: string;
  message: string;
  status?: RedemptionConnectStatus;
}

export interface RedemptionConnectPanelProps {
  getConnectStatus: () => Promise<RedemptionConnectStatus>;
  startConnectOnboarding: (returnUrl: string, refreshUrl: string) => Promise<RedemptionConnectOnboardResult>;
  simulateConnect?: (outcome: 'approved' | 'rejected' | 'reset') => Promise<RedemptionConnectSimulateResult>;
  /** Show when user returns from Stripe (?connect=complete|refresh) */
  connectReturnHint?: string | null;
  onStatusChange?: (status: RedemptionConnectStatus) => void;
}

const BLOCKER_LABELS: Record<string, string> = {
  CONNECT_ONBOARDING_REQUIRED: 'Start buyback settlement setup (Stripe Connect)',
  CONNECT_DETAILS_REQUIRED: 'Complete Connect identity and bank details',
  CONNECT_PAYOUTS_DISABLED: 'Connect settlement transfers must be enabled',
  W9_TIN_REQUIRED: 'W-9 / tax information (TIN) on file',
};

const TIN_LABELS: Record<string, string> = {
  TIN_PENDING: 'Tax ID (W-9) not submitted',
  TIN_PENDING_REVIEW: 'Tax ID pending review',
  W9_ON_FILE: 'W-9 on file',
  TIN_VERIFIED: 'Tax ID verified',
};

function buildReturnUrl(segment: 'complete' | 'refresh'): string {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.href);
  url.searchParams.set('connect', segment);
  return url.toString();
}

const RedemptionConnectPanel: React.FC<RedemptionConnectPanelProps> = ({
  getConnectStatus,
  startConnectOnboarding,
  simulateConnect,
  connectReturnHint,
  onStatusChange,
}) => {
  const [status, setStatus] = useState<RedemptionConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getConnectStatus();
      setStatus(s);
      onStatusChange?.(s);
    } catch {
      setError('Could not load payout setup status.');
    } finally {
      setLoading(false);
    }
  }, [getConnectStatus, onStatusChange]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (connectReturnHint === 'complete') {
      setMessage('Returned from Stripe. Refreshing payout setup status…');
      void load();
    } else if (connectReturnHint === 'refresh') {
      setMessage('Stripe onboarding session expired. You can restart setup below.');
    }
  }, [connectReturnHint, load]);

  const handleLiveOnboard = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await startConnectOnboarding(buildReturnUrl('complete'), buildReturnUrl('refresh'));
      if (result.success && result.onboardingUrl) {
        window.open(result.onboardingUrl, '_blank', 'noopener,noreferrer');
        setMessage('Stripe Connect opened in a new tab. Return here when finished.');
      } else if (result.simulationAvailable) {
        setError(result.errorMessage ?? 'Stripe unavailable locally. Use dev simulation below.');
      } else {
        setError(result.errorMessage ?? 'Unable to start Connect onboarding.');
      }
    } catch {
      setError('Connect onboarding request failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleSimulate = async (outcome: 'approved' | 'rejected' | 'reset') => {
    if (!simulateConnect) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await simulateConnect(outcome);
      if (result.success && result.status) {
        setStatus(result.status);
        onStatusChange?.(result.status);
        setMessage(result.message);
      } else {
        setError(result.message || 'Simulation failed.');
      }
    } catch {
      setError('Simulation request failed.');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!status?.requireConnectSetup) {
    return null;
  }

  const ready = status.isReadyForPayout;

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BankIcon fontSize="small" color="primary" />
            Payout setup for buyback (ACH / W-9)
          </Typography>
          {ready ? (
            <Chip size="small" color="success" label="Ready for buyback settlement" />
          ) : (
            <Chip size="small" color="warning" variant="outlined" label="Setup required before buyback" />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary">
          Before Qix Credits convert to U.S. dollars, Stripe Connect collects bank details and W-9 tax information.
          This is separate from platform ID verification.
        </Typography>

        {message && <Alert severity="info" onClose={() => setMessage(null)}>{message}</Alert>}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {status.disabledReason && !ready && (
          <Alert severity="warning">{status.disabledReason}</Alert>
        )}

        <List dense disablePadding>
          {[
            { met: status.hasConnectAccount, label: 'Stripe Connect account created' },
            { met: status.detailsSubmitted, label: 'Identity & bank details submitted' },
            { met: status.payoutsEnabled, label: 'ACH payouts enabled' },
            {
              met: status.tinStatus === 'W9_ON_FILE' || status.tinStatus === 'TIN_VERIFIED',
              label: TIN_LABELS[status.tinStatus] ?? status.tinStatus,
            },
          ].map((row) => (
            <ListItem key={row.label} disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {row.met ? <CheckIcon color="success" fontSize="small" /> : <PendingIcon color="disabled" fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={row.label} />
            </ListItem>
          ))}
        </List>

        {!ready && status.payoutBlockers.length > 0 && (
          <Alert severity="warning" icon={<ErrorIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Still needed:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {status.payoutBlockers.map((b) => (
                <li key={b}>{BLOCKER_LABELS[b] ?? b}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
          <Button variant="contained" onClick={handleLiveOnboard} disabled={busy}>
            {busy ? 'Starting…' : status.stripeConfigured ? 'Open Stripe payout setup' : 'Try Stripe payout setup'}
          </Button>
          <Button variant="outlined" size="small" onClick={() => void load()} disabled={busy}>
            Refresh status
          </Button>
        </Stack>

        {status.devSimulationAvailable && simulateConnect && (
          <Alert severity="info" variant="outlined">
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Local dev:</strong> Stripe Connect often fails on localhost. Use simulation to test approval or rejection without live Stripe.
              {status.usedSimulation && status.simulationMode && (
                <> Current simulation: <strong>{status.simulationMode}</strong>.</>
              )}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button size="small" variant="contained" color="success" disabled={busy} onClick={() => void handleSimulate('approved')}>
                Simulate approved
              </Button>
              <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => void handleSimulate('rejected')}>
                Simulate rejected
              </Button>
              <Button size="small" variant="text" disabled={busy} onClick={() => void handleSimulate('reset')}>
                Reset setup
              </Button>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

export default RedemptionConnectPanel;
